import React, { useState } from 'react';
import {
  Sparkles, Plus, Mail, Phone, MessageSquare, Video, Trash2, FileText, Clock,
  ChevronDown, Database, Flame, Snowflake, HelpCircle, Edit2, CheckCircle,
  GripVertical, Users, DollarSign, ArrowDownRight, Target, TrendingUp, Star,
  ArrowUpRight, Folder, Lock, Eye, Calendar, Building2, Coins, Briefcase,
  Layers, Megaphone, Globe, Twitter, Linkedin, Youtube, Instagram, Mic,
  Newspaper, Landmark, PieChart, Crown, ShieldCheck, AlertCircle,
} from 'lucide-react';

/**
 * VaultSamplePreview — empty-state demo wrapping rich illustrative cards in a
 * dashed coloured frame. The "samples" array can be either:
 *   • simple objects {heading, lines:[]}  → renders the lightweight tile
 *   • investor objects {kind:'investor', ...}  → renders the FULL investor card
 *     layout (avatar, signal score, status pill, shares/invested, action row)
 * matching the live Investors tab so Robert + Rebecca see exactly the layout
 * their real data will land in.
 */

const STATUS_COLORS = {
  // pink = prospect_lead / special engagement
  pink:  { border: '#EC4899', tint: '#FCE7F3', text: '#9D174D' },
  // blue = cool/warm signal
  blue:  { border: '#3B82F6', tint: '#DBEAFE', text: '#1E40AF' },
  // green = active/confirmed
  green: { border: '#10B981', tint: '#D1FAE5', text: '#065F46' },
};

const InvestorSampleCard = ({ card, testid }) => {
  const c = STATUS_COLORS[card.statusColor] || STATUS_COLORS.blue;
  const [open, setOpen] = useState(false);
  const SignalIcon = card.signalIcon === 'flame' ? Flame : card.signalIcon === 'snow' ? Snowflake : Sparkles;
  return (
    <div className="rounded-xl bg-white relative overflow-hidden text-left" data-testid={testid}
         style={{ border: `2px solid ${c.border}`, boxShadow: `0 1px 0 ${c.border}22 inset` }}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${c.border}22`, color: c.border }}>example</span>

      {/* Top strip: signal + Why? */}
      <div className="px-3 pt-3 pb-2 flex items-center justify-between" style={{ backgroundColor: c.tint }}>
        <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: c.text }}>
          <SignalIcon size={11} />
          {card.signal}
        </div>
        <button type="button" className="text-[10px] font-semibold inline-flex items-center gap-0.5 opacity-60 hover:opacity-100"
                style={{ color: c.text }}>
          <HelpCircle size={10}/> Why?
        </button>
      </div>

      {/* Body */}
      <div className="p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white shrink-0 text-sm"
               style={{ backgroundColor: c.border }}>
            {card.name[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-gray-900 truncate">{card.name}</div>
            <div className="text-[11px] text-gray-500 truncate">{card.firm}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">Last activity {card.lastActivity}</div>
          </div>
          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0"
                style={{ backgroundColor: c.border, color: 'white' }}>{card.status}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 mt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div>
            <div className="text-[9px] text-gray-400 uppercase tracking-wider">Shares</div>
            <div className="text-xs font-bold text-gray-900">{card.shares}</div>
          </div>
          <div>
            <div className="text-[9px] text-gray-400 uppercase tracking-wider">Invested</div>
            <div className="text-xs font-bold text-gray-900">{card.invested}</div>
          </div>
        </div>

        <div className="space-y-0.5 pt-1.5 text-[11px] text-gray-600">
          <div className="flex items-center gap-1.5"><Mail size={10}/><span className="truncate">{card.email}</span></div>
          {card.phone && <div className="flex items-center gap-1.5"><Phone size={10}/><span>{card.phone}</span></div>}
        </div>

        <div className="text-[10px] text-gray-400 italic">No vault activity yet</div>

        {/* Action row */}
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
          <button type="button" className="text-[10px] font-semibold px-1.5 py-1 rounded inline-flex items-center gap-1 border" style={{ borderColor: '#E5E7EB' }}>
            <FileText size={10}/> Recommend Offer
          </button>
          <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] font-semibold px-1.5 py-1 rounded inline-flex items-center gap-1 border" style={{ borderColor: '#E5E7EB' }}>
            <Clock size={10}/> IR Timeline <ChevronDown size={9} className={`transition-transform ${open?'rotate-180':''}`}/>
          </button>
          <button type="button" className="text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-1 text-white" style={{ backgroundColor: c.border }}>
            <MessageSquare size={10}/> Message
          </button>
          <button type="button" className="p-1 rounded text-gray-400 hover:text-gray-700"><Video size={11}/></button>
          <button type="button" className="p-1 rounded text-gray-400 hover:text-red-600"><Trash2 size={11}/></button>
        </div>

        {/* Dropdown content (matches "IR Timeline" expand from screenshot) */}
        {open && (
          <div className="mt-2 p-2 rounded text-[10px] space-y-1" style={{ backgroundColor: c.tint, color: c.text }}>
            <div>• Pitch deck shared · 3 days ago</div>
            <div>• Dataroom invite accepted · 5 days ago</div>
            <div>• Intro email · 12 days ago</div>
          </div>
        )}

        <div className="text-[9px] text-gray-400 inline-flex items-center gap-1 pt-1">
          <Database size={9}/> Data Room — 6-digit code
        </div>
      </div>
    </div>
  );
};

const SimpleSampleCard = ({ card, accent, testid }) => (
  <div className="rounded-lg bg-white p-3 shadow-sm relative overflow-hidden" style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
    <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
    <div className="text-sm font-bold text-gray-900 mb-2 pr-12">{card.heading}</div>
    {(card.lines || []).map((line, j) => <div key={j} className="text-[12px] text-gray-600 leading-relaxed">{line}</div>)}
  </div>
);

// ─── Rich Data Room Sample Card ─────────────────────────────────────────────
// Mirrors a Data Room row in the live tab — file/folder name, type pill,
// access stats, last viewer, lock icon for protected docs.
const DOC_TYPE_STYLE = {
  pdf:   { bg: '#FEE2E2', text: '#991B1B', short: 'PDF' },
  xlsx:  { bg: '#D1FAE5', text: '#065F46', short: 'XLSX' },
  docx:  { bg: '#DBEAFE', text: '#1E40AF', short: 'DOCX' },
  pptx:  { bg: '#FFEDD5', text: '#9A3412', short: 'PPTX' },
  folder:{ bg: '#F3E8FF', text: '#6B21A8', short: 'FOLDER' },
};
const DataRoomSampleCard = ({ card, accent, testid }) => {
  const ds = DOC_TYPE_STYLE[card.docType] || DOC_TYPE_STYLE.pdf;
  const Icon = card.docType === 'folder' ? Folder : FileText;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 pr-12">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: ds.bg }}>
          <Icon size={18} style={{ color: ds.text }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <p className="text-sm font-bold text-gray-900 truncate">{card.name}</p>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{ backgroundColor: ds.bg, color: ds.text }}>{ds.short}</span>
            {card.protected && <Lock size={10} className="text-amber-600" />}
          </div>
          <p className="text-[11px] text-gray-600">{card.subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Views</div>
          <div className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1">
            <Eye size={11} className="text-gray-400" />{card.views || 0}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Size</div>
          <div className="text-sm font-bold text-gray-900">{card.size}</div>
        </div>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Last view</div>
          <div className="text-[11px] font-semibold text-gray-900 truncate" title={card.lastViewer}>{card.lastView}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Rich Fundraise Sample Card ─────────────────────────────────────────────
// Mirrors a Fundraise round card — round label, status pill, target raise,
// progress bar against target, lead investor + close date.
const FUND_STATUS = {
  planning: { bg: '#F3F4F6', text: '#374151', label: 'Planning' },
  active:   { bg: '#DBEAFE', text: '#1E40AF', label: 'Active' },
  closed:   { bg: '#D1FAE5', text: '#065F46', label: 'Closed' },
};
const FundraiseSampleCard = ({ card, accent, testid }) => {
  const s = FUND_STATUS[card.status] || FUND_STATUS.planning;
  const pct = Math.min(100, Math.round(((card.raised || 0) / Math.max(card.target || 1, 1)) * 100));
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 mb-3 pr-12">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${accent}15` }}>
          <Coins size={18} style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <p className="text-sm font-bold text-gray-900 truncate">{card.round}</p>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{ backgroundColor: s.bg, color: s.text }}>{s.label}</span>
          </div>
          <p className="text-[11px] text-gray-600">Lead: <span className="font-semibold">{card.lead}</span></p>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-gray-600">Raised</span>
          <span className="font-bold text-gray-900">{card.raisedLabel} <span className="text-gray-400">/ {card.targetLabel}</span></span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
          <div className="h-full rounded-full transition-all"
               style={{ width: `${pct}%`, backgroundColor: card.status === 'closed' ? '#10B981' : accent }} />
        </div>
        <div className="flex items-center justify-between pt-1.5 text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1"><Calendar size={10} /> {card.closeDate}</span>
          <span>Valuation {card.valuation}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Rich Acquisitions Sample Card ──────────────────────────────────────────
// Mirrors an Acquisition target card — name + sector + risk pill,
// strategic-vs-defensive tag, key stats (ARR, overlap, fit score).
const ACQ_TAG_STYLE = {
  strategic: { bg: '#EDE9FE', text: '#5B21B6', label: 'Strategic' },
  defensive: { bg: '#FFE4E6', text: '#9F1239', label: 'Defensive' },
  tuck_in:   { bg: '#DBEAFE', text: '#1E40AF', label: 'Tuck-in' },
};
const AcquisitionSampleCard = ({ card, accent, testid }) => {
  const tag = ACQ_TAG_STYLE[card.tag] || ACQ_TAG_STYLE.strategic;
  const fit = Math.max(0, Math.min(100, card.fitScore || 0));
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 mb-3 pr-12">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${accent}15` }}>
          <Building2 size={18} style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <p className="text-sm font-bold text-gray-900 truncate">{card.name}</p>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{ backgroundColor: tag.bg, color: tag.text }}>{tag.label}</span>
          </div>
          <p className="text-[11px] text-gray-600 inline-flex items-center gap-1">
            <Briefcase size={10} /> {card.sector}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2.5">
        <div className="text-center bg-gray-50 rounded-lg py-2">
          <div className="text-[9px] text-gray-500 uppercase tracking-wider">ARR</div>
          <div className="text-xs font-bold text-gray-900">{card.arr}</div>
        </div>
        <div className="text-center bg-gray-50 rounded-lg py-2">
          <div className="text-[9px] text-gray-500 uppercase tracking-wider">Overlap</div>
          <div className="text-xs font-bold text-gray-900">{card.overlap}</div>
        </div>
        <div className="text-center bg-gray-50 rounded-lg py-2">
          <div className="text-[9px] text-gray-500 uppercase tracking-wider">Risk</div>
          <div className="text-xs font-bold" style={{ color: card.risk === 'high' ? '#DC2626' : card.risk === 'med' ? '#F59E0B' : '#10B981' }}>
            {card.risk?.toUpperCase() || 'LOW'}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-gray-500 inline-flex items-center gap-1"><Layers size={9} /> Strategic fit</span>
          <span className="font-bold text-gray-900">{fit}%</span>
        </div>
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
          <div className="h-full rounded-full" style={{ width: `${fit}%`, backgroundColor: accent }} />
        </div>
      </div>
    </div>
  );
};
// ─── Rich NASDAQ / IPO Sample Card ──────────────────────────────────────────
// Mirrors a single IPO checklist requirement: category pill, requirement
// title, met/in-progress/blocked status, owner, target date.
const NASDAQ_CATEGORY = {
  Financial:    { bg: '#DBEAFE', text: '#1E40AF' },
  Governance:   { bg: '#FEF3C7', text: '#92400E' },
  Compliance:   { bg: '#EDE9FE', text: '#5B21B6' },
  'SEC Filings':{ bg: '#FEE2E2', text: '#991B1B' },
  'IPO Process':{ bg: '#D1FAE5', text: '#065F46' },
};
const NASDAQ_STATUS = {
  met:         { bg: '#10B981', icon: CheckCircle, label: 'Met' },
  in_progress: { bg: '#F59E0B', icon: Clock,        label: 'In progress' },
  blocked:     { bg: '#DC2626', icon: AlertCircle, label: 'Blocked' },
  not_started: { bg: '#9CA3AF', icon: Clock,        label: 'Not started' },
};
const NasdaqSampleCard = ({ card, accent, testid }) => {
  const cat = NASDAQ_CATEGORY[card.category] || { bg: '#F3F4F6', text: '#374151' };
  const st = NASDAQ_STATUS[card.status] || NASDAQ_STATUS.not_started;
  const StIcon = st.icon;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 pr-12 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${st.bg}15`, border: `1px solid ${st.bg}` }}>
          <StIcon size={18} style={{ color: st.bg }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{ backgroundColor: cat.bg, color: cat.text }}>{card.category}</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                  style={{ backgroundColor: st.bg }}>{st.label}</span>
          </div>
          <p className="text-sm font-bold text-gray-900 leading-snug">{card.title}</p>
        </div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t text-[10px]" style={{ borderColor: '#F3F4F6' }}>
        <span className="inline-flex items-center gap-1 text-gray-500">
          <Landmark size={11} /> {card.owner}
        </span>
        <span className="font-semibold text-gray-700">{card.target}</span>
      </div>
    </div>
  );
};

// ─── Rich PR & Social Sample Card ───────────────────────────────────────────
// Mirrors a PRTrackerTab activity row: platform icon + title + date + reach /
// engagement / cost trio at the bottom.
const PR_PLATFORM = {
  LinkedIn:    { Icon: Linkedin,  bg: '#DBEAFE', color: '#0A66C2' },
  'Twitter/X': { Icon: Twitter,   bg: '#E0F2FE', color: '#1DA1F2' },
  Twitter:     { Icon: Twitter,   bg: '#E0F2FE', color: '#1DA1F2' },
  Instagram:   { Icon: Instagram, bg: '#FCE7F3', color: '#E1306C' },
  YouTube:     { Icon: Youtube,   bg: '#FEE2E2', color: '#FF0000' },
  Podcast:     { Icon: Mic,       bg: '#EDE9FE', color: '#7C3AED' },
  Press:       { Icon: Newspaper, bg: '#F3F4F6', color: '#374151' },
  TikTok:      { Icon: Megaphone, bg: '#FCE7F3', color: '#000000' },
};
const PRSocialSampleCard = ({ card, accent, testid }) => {
  const p = PR_PLATFORM[card.platform] || PR_PLATFORM.Press;
  const PIcon = p.Icon;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 pr-12">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: p.bg }}>
          <PIcon size={18} style={{ color: p.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                  style={{ backgroundColor: p.bg, color: p.color }}>{card.platform}</span>
            <span className="text-[10px] text-gray-500">{card.date}</span>
          </div>
          <p className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">{card.title}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Reach</div>
          <div className="text-sm font-bold text-gray-900 inline-flex items-center justify-center gap-1">
            <Globe size={11} className="text-gray-400" /> {card.reach}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Engagement</div>
          <div className="text-sm font-bold text-gray-900 inline-flex items-center justify-center gap-1">
            <Star size={11} className="text-gray-400" /> {card.engagement}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">Cost</div>
          <div className="text-sm font-bold text-gray-900">{card.cost}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Rich Cap Table Sample Card ─────────────────────────────────────────────
// Mirrors a single equity holder row: name + role + ownership %, share
// count + class, plus a thin progress bar visualising the slice of the pie.
const HOLDER_ROLE = {
  founder:    { Icon: Crown,      color: '#F59E0B', bg: '#FEF3C7', label: 'Founder' },
  investor:   { Icon: TrendingUp, color: '#3B82F6', bg: '#DBEAFE', label: 'Investor' },
  esop:       { Icon: Users,      color: '#10B981', bg: '#D1FAE5', label: 'ESOP' },
  advisor:    { Icon: ShieldCheck,color: '#8B5CF6', bg: '#EDE9FE', label: 'Advisor' },
  treasury:   { Icon: PieChart,   color: '#6B7280', bg: '#F3F4F6', label: 'Treasury' },
};
const CapTableSampleCard = ({ card, accent, testid }) => {
  const r = HOLDER_ROLE[card.role] || HOLDER_ROLE.investor;
  const RIcon = r.Icon;
  const pct = Math.max(0, Math.min(100, card.pct || 0));
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
         style={{ border: `1px solid ${accent}40` }} data-testid={testid}>
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <div className="flex items-start gap-3 pr-12 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: r.bg }}>
          <RIcon size={18} style={{ color: r.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">{card.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{ backgroundColor: r.bg, color: r.color }}>{r.label}</span>
            <span className="text-[10px] text-gray-500">{card.shareClass}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-black" style={{ color: r.color }}>{pct}%</div>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: r.color }} />
        </div>
        <div className="flex items-center justify-between text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Layers size={10} /> {card.shares} shares
          </span>
          <span>{card.notes}</span>
        </div>
      </div>
    </div>
  );
};
// Mirrors the live KPI tile in SecureVault.js (KPITab → kpiCards grid):
//   icon + trend badge top row · big bold value · uppercase label
// Plus an inline mini sparkline so the founder can see what their real
// historical KPI cards will look like once they start logging months.
const KPI_ICON_MAP = {
  users: Users, dollar: DollarSign, churn: ArrowDownRight,
  target: Target, trending: TrendingUp, star: Star,
};

const Sparkline = ({ values = [], color = '#3B82F6' }) => {
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const w = 96, h = 28;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const pts = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="mt-2 block">
      <polyline fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      {values.map((v, i) => (
        <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * (h - 4) - 2).toFixed(1)} r={i === values.length - 1 ? 2.2 : 1.4} fill={color} />
      ))}
    </svg>
  );
};

const KpiSampleCard = ({ card, accent, testid }) => {
  const Icon = KPI_ICON_MAP[card.icon] || TrendingUp;
  const trendPositive = (card.trend || 0) >= 0;
  return (
    <div
      className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden"
      style={{ border: `1px solid ${card.color || accent}40` }}
      data-testid={testid}
    >
      <span
        className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
        style={{ backgroundColor: `${card.color || accent}22`, color: card.color || accent }}
      >example</span>

      <div className="flex items-center justify-between mb-1.5 pr-12">
        <Icon size={14} style={{ color: card.color || accent }} />
        {card.trend !== undefined && (
          <span className={`text-xs font-bold inline-flex items-center gap-0.5 ${trendPositive ? (card.goodIfUp === false ? 'text-red-500' : 'text-emerald-600') : (card.goodIfUp === false ? 'text-emerald-600' : 'text-red-500')}`}>
            {trendPositive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {card.trend > 0 ? '+' : ''}{card.trend}%
          </span>
        )}
      </div>
      <p className="text-xl font-black text-gray-900 leading-tight">{card.value}</p>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mt-0.5">{card.label}</p>
      {card.sparkline && <Sparkline values={card.sparkline} color={card.color || accent} />}
      {card.sub && <p className="text-[10px] text-gray-500 mt-1.5 italic">{card.sub}</p>}
    </div>
  );
};

// ─── Rich Milestone Sample Card ──────────────────────────────────────────────
// Mirrors the live Milestone row in SecureVault.js (MilestonesTab):
//   round toggle button · title · category pill · description · date · edit/delete
// Upcoming variant uses dashed border + drag handle; achieved variant uses
// emerald background with line-through title and check filled in.
const CATEGORY_STYLE = {
  product: { bg: '#DBEAFE', text: '#1E40AF' },
  growth: { bg: '#D1FAE5', text: '#065F46' },
  revenue: { bg: '#EDE9FE', text: '#5B21B6' },
  fundraising: { bg: '#FEF3C7', text: '#92400E' },
};

const MilestoneSampleCard = ({ card, accent, testid }) => {
  const cat = CATEGORY_STYLE[card.category] || { bg: '#F3F4F6', text: '#374151' };
  if (card.achieved) {
    return (
      <div
        className="bg-emerald-50 rounded-xl p-3.5 flex items-center gap-3 relative overflow-hidden"
        style={{ border: '1px solid #A7F3D0' }}
        data-testid={testid}
      >
        <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ backgroundColor: '#A7F3D0', color: '#065F46' }}>example</span>
        <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <CheckCircle size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0 pr-12">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <p className="text-sm font-bold text-gray-900 line-through opacity-70 truncate">{card.title}</p>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold capitalize"
                  style={{ backgroundColor: cat.bg, color: cat.text }}>{card.category}</span>
          </div>
          {card.description && <p className="text-[11px] text-gray-600 line-clamp-2">{card.description}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {card.date && <p className="text-[10px] text-gray-600 whitespace-nowrap">{card.date}</p>}
          <Edit2 size={11} className="text-gray-400" />
        </div>
      </div>
    );
  }
  return (
    <div
      className="bg-white rounded-xl p-3.5 flex items-center gap-3 relative overflow-hidden transition-all"
      style={{ border: `2px dashed ${accent}55` }}
      data-testid={testid}
    >
      <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accent}22`, color: accent }}>example</span>
      <GripVertical size={13} className="text-gray-300 flex-shrink-0" />
      <div className="w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0">
        <CheckCircle size={13} className="text-gray-300" />
      </div>
      <div className="flex-1 min-w-0 pr-12">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <p className="text-sm font-bold text-gray-900 truncate">{card.title}</p>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold capitalize"
                style={{ backgroundColor: cat.bg, color: cat.text }}>{card.category}</span>
        </div>
        {card.description && <p className="text-[11px] text-gray-600 line-clamp-2">{card.description}</p>}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {card.date && <p className="text-[10px] text-gray-600 whitespace-nowrap">{card.date}</p>}
        <Edit2 size={11} className="text-gray-400" />
        <Trash2 size={11} className="text-gray-300" />
      </div>
    </div>
  );
};

const VaultSamplePreview = ({ accent = '#F5831F', title, addLabel, samples = [], dataTestid = 'vault-sample-preview', cols }) => {
  // Allow callers to override the grid density; KPIs render best on a denser
  // 6-up grid because each tile is compact, while milestones need a vertical
  // stack to mirror the live list. Defaults match the previous 3-col layout.
  const gridClass = cols === 'kpi'
    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'
    : cols === 'stack'
      ? 'space-y-2'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3';
  return (
    <div className="relative rounded-2xl p-4 sm:p-5" data-testid={dataTestid}
         style={{ border: `2px dashed ${accent}55`, backgroundColor: `${accent}08` }}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4 pb-3 border-b" style={{ borderColor: `${accent}33` }}>
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: accent }} />
          <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: accent }}>Sample preview</span>
          <span className="text-xs text-gray-600 hidden sm:inline">— {title}</span>
        </div>
        <span className="text-[11px] text-gray-500 inline-flex items-center gap-1">
          <Plus size={11} /> {addLabel || 'Use the Add button above to begin'}
        </span>
      </div>

      <div className={gridClass}>
        {samples.map((s, i) => {
          if (s.kind === 'investor') return <InvestorSampleCard key={i} card={s} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'kpi') return <KpiSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'milestone') return <MilestoneSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'dataroom') return <DataRoomSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'fundraise') return <FundraiseSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'acquisition') return <AcquisitionSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'nasdaq') return <NasdaqSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'pr') return <PRSocialSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          if (s.kind === 'captable') return <CapTableSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
          return <SimpleSampleCard key={i} card={s} accent={accent} testid={`${dataTestid}-card-${i}`} />;
        })}
      </div>

      <p className="text-[11px] text-gray-500 mt-4 text-center italic">
        These cards are illustrative only — your real entries will appear here in this exact layout.
      </p>
    </div>
  );
};

export default VaultSamplePreview;

// ─── Rich Investor samples — matches the real Investor card layout exactly
// (pink/blue/green status colours, signal score with Hot/Cool icon, avatar,
// firm + last activity, status pill, shares/invested grid, email/phone with
// icons, action row with Recommend Offer / IR Timeline dropdown / Message /
// Video / Delete, data room footer). Wired to render via the InvestorSampleCard
// component when card.kind === 'investor'.
export const SAMPLES_INVESTORS = [
  {
    kind: 'investor', statusColor: 'pink',
    signal: 'Hot · Signal 78', signalIcon: 'flame',
    name: 'Marcus Chen', firm: 'Sequoia · Menlo Park',
    lastActivity: '22h ago', status: 'prospect_lead',
    shares: '0', invested: '$0',
    email: 'marcus@sequoia.test', phone: '+1 415 555 0142',
  },
  {
    kind: 'investor', statusColor: 'blue',
    signal: 'Cool · Signal 47.5', signalIcon: 'snow',
    name: 'Jane Doe', firm: 'Brave VC · London',
    lastActivity: '3 days ago', status: 'prospect',
    shares: '0', invested: '$0',
    email: 'jane@bravevc.example', phone: '+44 7700 900142',
  },
  {
    kind: 'investor', statusColor: 'green',
    signal: 'Signal 38.2', signalIcon: 'sparkle',
    name: 'Sarah Chen', firm: 'Palo Alto · United States',
    lastActivity: '09/04/2026', status: 'active',
    shares: '12,500', invested: '$250,000',
    email: 'sarah.chen@venturecap.io', phone: '+1 415 555 1234',
  },
];

// ─── Rich Milestone samples — matches the live MilestonesTab row layout
// (round toggle, title with category pill, description, target date,
// edit/delete icons; achieved variant gets emerald background + line-through
// title and a green check). Wired to render via the MilestoneSampleCard
// component when card.kind === 'milestone'. Sample order mimics the live
// tab: upcoming dashed rows first, achieved row last.
export const SAMPLES_MILESTONES = [
  {
    kind: 'milestone',
    title: 'Schools pilot signed',
    category: 'growth',
    description: 'Sign first paid schools pilot · 3 schools in active discussion.',
    date: 'Jul 2026',
    achieved: false,
  },
  {
    kind: 'milestone',
    title: 'Series Seed close',
    category: 'fundraising',
    description: 'Close $1.2M Seed · narrative locked, 5 LOIs in flight.',
    date: 'Q4 2026',
    achieved: false,
  },
  {
    kind: 'milestone',
    title: 'Stripe LIVE',
    category: 'revenue',
    description: '$420 first 7-day revenue · live billing flow confirmed.',
    date: '18 Jun 2026',
    achieved: true,
  },
];

// ─── Rich KPI samples — matches the live KPI tile in KPITab exactly
// (icon + trend badge top row, big bold value, uppercase label, mini
// sparkline). Six tiles cover the founder's full executive KPI deck:
// MAU, MRR, Churn, CAC, LTV, NPS — same order as the live grid.
export const SAMPLES_KPIS = [
  {
    kind: 'kpi', icon: 'users', label: 'MAU', value: '12,480',
    trend: 18, goodIfUp: true, color: '#3B82F6',
    sparkline: [4200, 5100, 6400, 8200, 9800, 11200, 12480],
    sub: 'Goal: 25,000 by Sep',
  },
  {
    kind: 'kpi', icon: 'dollar', label: 'MRR', value: '$18,420',
    trend: 12, goodIfUp: true, color: '#10B981',
    sparkline: [5200, 7100, 9400, 11800, 14200, 16800, 18420],
    sub: 'ARR: $221K',
  },
  {
    kind: 'kpi', icon: 'churn', label: 'Churn Rate', value: '3.2%',
    trend: -1.4, goodIfUp: false, color: '#EF4444',
    sparkline: [6.1, 5.4, 4.8, 4.2, 3.9, 3.5, 3.2],
    sub: 'Benchmark: 5.5%',
  },
  {
    kind: 'kpi', icon: 'target', label: 'CAC', value: '$48',
    trend: -8, goodIfUp: false, color: '#F59E0B',
    sparkline: [82, 74, 68, 61, 55, 51, 48],
    sub: 'Payback < 4 months',
  },
  {
    kind: 'kpi', icon: 'trending', label: 'LTV', value: '$284',
    trend: 22, goodIfUp: true, color: '#8B5CF6',
    sparkline: [160, 178, 195, 218, 241, 263, 284],
    sub: 'LTV:CAC = 5.9x',
  },
  {
    kind: 'kpi', icon: 'star', label: 'NPS', value: '+62',
    trend: 9, goodIfUp: true, color: '#EC4899',
    sparkline: [38, 44, 49, 53, 56, 59, 62],
    sub: 'World-class > +50',
  },
];

// ─── Other tabs still use simple sample cards (next sprint will upgrade these
// to match each tab's full visual format — milestones with chart, KPIs with
// metrics + sparkline, etc. — once the user signs off on the Investors mock.)

export const SAMPLES_FUNDRAISE = [
  {
    kind: 'fundraise',
    round: 'Pre-Seed',
    status: 'closed',
    lead: 'Local angel syndicate',
    raised: 180000,
    target: 250000,
    raisedLabel: '$180K',
    targetLabel: '$250K',
    closeDate: 'Closed Mar 2026',
    valuation: '$3.2M',
  },
  {
    kind: 'fundraise',
    round: 'Seed',
    status: 'active',
    lead: 'Brave VC · warming up',
    raised: 420000,
    target: 1200000,
    raisedLabel: '$420K',
    targetLabel: '$1.2M',
    closeDate: 'Target close Q4 2026',
    valuation: '$8M cap',
  },
  {
    kind: 'fundraise',
    round: 'Bridge SAFE',
    status: 'planning',
    lead: 'Optional · founder discretion',
    raised: 0,
    target: 300000,
    raisedLabel: '$0',
    targetLabel: '$300K',
    closeDate: 'Open when needed',
    valuation: '20% discount',
  },
];

export const SAMPLES_ACQUISITIONS = [
  {
    kind: 'acquisition',
    name: 'BoostKidz Ltd',
    sector: 'Youth wellness app',
    tag: 'strategic',
    arr: '$180K',
    overlap: '60%',
    risk: 'low',
    fitScore: 84,
  },
  {
    kind: 'acquisition',
    name: 'SocialSparks',
    sector: 'AI chat for teens',
    tag: 'defensive',
    arr: '$90K',
    overlap: '35%',
    risk: 'high',
    fitScore: 71,
  },
  {
    kind: 'acquisition',
    name: 'KindMind Bots',
    sector: 'Empathic AI conversation',
    tag: 'tuck_in',
    arr: '$42K',
    overlap: '78%',
    risk: 'med',
    fitScore: 66,
  },
];

export const SAMPLES_CONTACT_LOG = [
  { heading: 'Sarah Chen · email · 12 Jun', lines: ['Subject: HiBFF investor deck', 'Outcome: positive — wants follow-up call', 'Next step: schedule for Tue'] },
  { heading: 'Schools Lead · call · 10 Jun', lines: ['Duration: 32 min', 'Outcome: pilot approved pending board', 'Owner: Robert'] },
];

export const SAMPLES_NOTES = [
  { heading: 'Investor objection patterns', lines: ['3 of 5 ask: "Why now vs CharacterAI?"', 'Drafted FAQ snippet for deck slide 14', 'Test next pitch'] },
  { heading: 'Schools pilot learnings', lines: ['Onboarding kit needs printed copy', 'Headteacher demo lands harder than slides', 'Free-trial pricing too cheap'] },
];

export const SAMPLES_CALENDAR = [
  { heading: 'Sarah Chen follow-up call', lines: ['Tue 25 Jun · 14:00 BST', '30 min · Zoom', 'Prep: bring updated TAM'] },
  { heading: 'Board update', lines: ['Last Mon of month · 18:00', 'Recurring', 'Topics: cash, milestones, asks'] },
];

export const SAMPLES_NASDAQ = [
  {
    kind: 'nasdaq',
    category: 'Financial',
    title: 'Three years audited financial statements (PCAOB)',
    status: 'in_progress',
    owner: 'External CFO',
    target: 'Q2 2028',
  },
  {
    kind: 'nasdaq',
    category: 'Governance',
    title: 'Independent board majority + audit committee charter',
    status: 'not_started',
    owner: 'Robert · Board recruit',
    target: 'Q4 2027',
  },
  {
    kind: 'nasdaq',
    category: 'SEC Filings',
    title: 'S-1 registration draft + EDGAR submission',
    status: 'blocked',
    owner: 'Securities counsel',
    target: '2028',
  },
];

export const SAMPLES_PR_SOCIAL = [
  {
    kind: 'pr',
    platform: 'LinkedIn',
    title: 'HiBFF reaches 5,000 active families · founder essay',
    date: '12 Jun 2026',
    reach: '12.4K',
    engagement: '684',
    cost: '$0',
  },
  {
    kind: 'pr',
    platform: 'Press',
    title: 'TechCrunch profile · teen mental-health AI done safely',
    date: '08 Jun 2026',
    reach: '218K',
    engagement: '3.1K',
    cost: '$0',
  },
  {
    kind: 'pr',
    platform: 'Podcast',
    title: 'Lenny Rachitsky · safe-by-default product design',
    date: '02 Jun 2026',
    reach: '92K',
    engagement: '1.4K',
    cost: '$500',
  },
];

export const SAMPLES_CAP_TABLE = [
  {
    kind: 'captable',
    name: 'Robert Parson',
    role: 'founder',
    pct: 80,
    shares: '8,000,000',
    shareClass: 'Common',
    notes: 'Founder · vested',
  },
  {
    kind: 'captable',
    name: 'Pre-seed angels',
    role: 'investor',
    pct: 12,
    shares: '1,200,000',
    shareClass: 'SAFE converted',
    notes: '3 holders pooled',
  },
  {
    kind: 'captable',
    name: 'ESOP pool',
    role: 'esop',
    pct: 8,
    shares: '800,000',
    shareClass: 'Options',
    notes: 'Reserved for team',
  },
];

export const SAMPLES_DATA_ROOM = [
  {
    kind: 'dataroom',
    name: 'Cap table v3.2',
    subtitle: 'Latest equity split incl. ESOP pool',
    docType: 'pdf',
    size: '184 KB',
    views: 14,
    lastView: '12 Jun',
    lastViewer: 'Sarah Chen',
    protected: true,
  },
  {
    kind: 'dataroom',
    name: 'Financial model — base case',
    subtitle: '3-year projection · MRR, CAC, LTV, burn',
    docType: 'xlsx',
    size: '92 KB',
    views: 9,
    lastView: '18 Jun',
    lastViewer: 'Marcus Chen',
    protected: true,
  },
  {
    kind: 'dataroom',
    name: 'Schools pilot contract',
    subtitle: 'DocuSign template attached',
    docType: 'pdf',
    size: '256 KB',
    views: 6,
    lastView: '22 May',
    lastViewer: 'Robert Parson',
    protected: false,
  },
];

export const SAMPLES_CAP_TABLE_LEGACY = [];
